import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import WomanReport from '../../../../components/icons/WomanReport'
import BehavioralAssessment from '../../../../components/BehavioralAssessment'
import CommunicationAssessment from '../../../../components/CommunicationAssessment'
import { fetchReports } from '../../actions'
import selectors from '../../selectors'

const breakpoints = {
  mobile: '320px',
  tablet: '768px',
  laptop: '1024px',
}

const ReportContainer = styled.div`
  width: 100%;
  margin: 88px auto 24px;

  @media (max-width: ${breakpoints.tablet}) {
    margin: 40px auto 24px;
  }
`

const HeaderSection = styled.div`
  width: 100%;
  height: 134px;
  background: #f7f7f7;
  border-radius: 25px;
  display: flex;
  align-items: left;
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 32px;
  margin-top: 64px;

  @media (max-width: ${breakpoints.tablet}) {
    height: auto;
    padding: 24px 16px;
    border-radius: 16px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 16px;
    margin-bottom: 24px;
  }
`

const IconSection = styled.div`
  position: absolute;
  left: 70px;
  bottom: 0;
  width: 130px;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  @media (max-width: ${breakpoints.tablet}) {
    display: none;
  }

  svg {
    width: 100%;
    height: auto;
  }
`

const ContentSection = styled.div`
  margin-left: 276px;
  flex: 1;

  @media (max-width: ${breakpoints.tablet}) {
    margin-left: 0;
  }
`

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #343e59;
  margin: 0 0 12px 0;
  font-family: 'Plus Jakarta Sans', sans-serif;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 20px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 18px;
  }
`

const Description = styled.p`
  font-size: 16px;
  color: #666666;
  margin: 0;
  line-height: 1.5;
  font-weight: 400;
  font-family: 'Plus Jakarta Sans', sans-serif;
  max-width: 800px;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 14px;
  }
`

const Button = styled.button`
  background: ${props =>
    props.variant === 'text' ? 'transparent' : '#007AFF'};
  color: ${props => (props.variant === 'text' ? '#007AFF' : 'white')};
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Plus Jakarta Sans', sans-serif;
  margin-top: 16px;
  text-decoration: ${props =>
    props.variant === 'text' ? 'underline' : 'none'};

  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }

  &:hover {
    background: ${props =>
      props.variant === 'text' ? 'transparent' : '#0056b3'};
    color: ${props => (props.variant === 'text' ? '#0056b3' : 'white')};
  }
`

const OlderReportSection = styled.div`
  margin-top: 24px;
  border-top: 1px solid #e5e5e5;

  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 20px;
    padding-top: 20px;
  }
`

const Reports = ({
  reports,
  loading,
  error,
  fetchReports,
  completedAssessments,
}) => {
  const [showOlderBehavioral, setShowOlderBehavioral] = useState(false)
  const [showOlderCommunication, setShowOlderCommunication] = useState(false)

  useEffect(() => {
    if (completedAssessments?.length > 0) {
      fetchReports()
    }
  }, [fetchReports, completedAssessments])

  // Early return if no reports or error
  if (loading) {
    return <div>Loading reports...</div>
  }

  if (error) {
    return <div>Error loading reports: {error}</div>
  }

  // Check if reports is an array and has items
  if (!Array.isArray(reports) || reports.length === 0) {
    return (
      <ReportContainer>
        <HeaderSection>
          <IconSection>
            <WomanReport />
          </IconSection>
          <ContentSection>
            <Title>Assessment Reports</Title>
            <Description>
              No reports available yet. Complete an assessment to see your
              results here.
            </Description>
          </ContentSection>
        </HeaderSection>
      </ReportContainer>
    )
  }

  // Sort and filter reports by type
  const sortedReports = reports.sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  )

  const communicationReports = sortedReports.filter(
    report => report.type.toLowerCase() === 'communication'
  )
  const behavioralReports = sortedReports.filter(
    report => report.type.toLowerCase() === 'behavior'
  )

  const latestCommunication = communicationReports[0]
  const olderCommunication = communicationReports[1]
  const hasOlderCommunication = communicationReports.length > 1

  const latestBehavioral = behavioralReports[0]
  const olderBehavioral = behavioralReports[1]
  const hasOlderBehavioral = behavioralReports.length > 1

  const renderReport = report => {
    if (!report) return null

    switch (report.type.toLowerCase()) {
      case 'behavior':
        return (
          <BehavioralAssessment
            data={report}
            categories={report.categories} // Backend provides categories array with name and level
            timestamp={report.timestamp}
            assessmentId={report.id}
          />
        )
      case 'communication':
        return (
          <CommunicationAssessment
            data={{
              timestamp: report.timestamp,
              englishLang: report.englishLang, // From video response score
              readingAbility: report.readingAbility, // From paragraph reading score
              comprehension: report.comprehension, // From audio question score
            }}
          />
        )
      default:
        return null
    }
  }

  return (
    <ReportContainer>
      <HeaderSection>
        <IconSection>
          <WomanReport />
        </IconSection>
        <ContentSection>
          <Title>Assessment Reports</Title>
          <Description>
            View your assessment results and track your progress over time.
          </Description>
        </ContentSection>
      </HeaderSection>

      {/* Communication Assessment Section */}
      {latestCommunication && (
        <>
          {renderReport(latestCommunication)}
          {hasOlderCommunication && !showOlderCommunication && (
            <Button
              variant="text"
              onClick={() => setShowOlderCommunication(true)}
            >
              See older communication scores
            </Button>
          )}
          {showOlderCommunication && olderCommunication && (
            <OlderReportSection>
              {renderReport(olderCommunication)}
              <Button
                variant="text"
                onClick={() => setShowOlderCommunication(false)}
              >
                Hide older communication scores
              </Button>
            </OlderReportSection>
          )}
        </>
      )}

      {/* Behavioral Assessment Section */}
      {latestBehavioral && (
        <>
          {renderReport(latestBehavioral)}
          {hasOlderBehavioral && !showOlderBehavioral && (
            <Button variant="text" onClick={() => setShowOlderBehavioral(true)}>
              See older behavioral scores
            </Button>
          )}
          {showOlderBehavioral && olderBehavioral && (
            <OlderReportSection>
              {renderReport(olderBehavioral)}
              <Button
                variant="text"
                onClick={() => setShowOlderBehavioral(false)}
              >
                Hide older behavioral scores
              </Button>
            </OlderReportSection>
          )}
        </>
      )}
    </ReportContainer>
  )
}

const mapStateToProps = state => ({
  reports: selectors.getReports(state),
  loading: selectors.getReportsLoading(state),
  error: selectors.getReportsError(state),
  completedAssessments: selectors.getCompletedAssessments(state),
})

const mapDispatchToProps = {
  fetchReports,
}

export default connect(mapStateToProps, mapDispatchToProps)(Reports)
